import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { FormatHookParams } from '/business/shared/components/pages/question/types';

import useEmissionSourceDropdown from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeEmissionSourceDropdown';
import useDatatypeBehaviour from '/business/shared/components/pages/question/formats/staticSharedFields/useDatatypeBehaviour';
import useHowWasCollected from '/business/shared/components/pages/question/formats/staticSharedFields/useHowWasCollected';
import useMakeQuantity from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeQuantity';
import useMakeSupplierName from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeSupplierName';
import useScope1Data from '/business/shared/components/pages/question/formats/staticSharedFields/useScope1Data';
import useScope3Data from '/business/shared/components/pages/question/formats/staticSharedFields/useScope3Data';
import useScopeCombined13Data from '/business/shared/components/pages/question/formats/staticSharedFields/useScopeCombined13Data';
import {
  QUESTION_BRANCH_DATA_KEY_COMBINED,
  QUESTION_BRANCH_DATA_KEY_SEPARATED,
  QUESTION_BRANCH_DATA_KEY_UNKNOWN,
  QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
  QUESTION_BRANCH_GROUP_KEY_INTENSITY,
} from '/business/shared/components/pages/question/questionEnums';
import WidgetIcon from '/business/shared/images/icons/expander-icon.svg';
import Button from '/shared/components/widgets/button/Button';
import WidgetAccordion from '/shared/components/widgets/widget-accordion/WidgetAccordion';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import {
  useGetEmissionSourceFields,
  useGetQuantityDropdownValues,
  useGetUnitDropdownValues,
} from '/business/shared/components/pages/question/useFieldUnits';
import { useGetEmissionsFromQuestion } from '/business/shared/hooks/useGetEmissionsFromQuestion';

export default function useFormatSharedSupplierScope1({
  question,
  questionFormatName,
  branchType,
  label,
  emissionSourcePlaceholder,
  sourceErrMsg,
}: FormatHookParams & { sourceErrMsg?: string }) {
  const { t } = useTranslation();
  const [dataTypeIntensityValue, setDataTypeIntensityValue] = useState(QUESTION_BRANCH_DATA_KEY_COMBINED);
  const [dataTypeEmissionsValue, setDataTypeEmissionsValue] = useState(QUESTION_BRANCH_DATA_KEY_COMBINED);
  const [emissionSource, setEmissionSource] = useState<string | null>(null);
  const [emissionSourceForAbsolute, setEmissionSourceForAbsolute] = useState<string | null>(null);

  // TODO: [WR] change to dynamic question apiKey
  const { mappedForDropdown } = useGetEmissionsFromQuestion({
    question: {
      apiKey:
        questionFormatName === 'vehicles'
          ? 'calculation-travel-emissions-company-vehicles-total-fuel-consumption'
          : 'calculation-building-emissions-other',
    },
  });

  const dataTypeBehaviour = useDatatypeBehaviour();
  const quantity = useMakeQuantity({
    quantityDropdownValues: useGetQuantityDropdownValues({
      questionFormatName,
      emissionSource: emissionSource,
      emissionSourcesArray: mappedForDropdown || [],
    }),
    subLabel: 'E.g. for 100 items, enter 100',
  });
  const quantityForAbsolute = useMakeQuantity({
    quantityDropdownValues: useGetQuantityDropdownValues({
      questionFormatName,
      emissionSource: emissionSourceForAbsolute,
      emissionSourcesArray: mappedForDropdown || [],
    }),
    subLabel: 'E.g. for 100 items, enter 100',
  });
  const emissionSourceDropdown = useEmissionSourceDropdown({
    valuesArray: useGetEmissionSourceFields({ questionFormatName, question }),
    emissionSourcePlaceholder,
    label,
    errMsg: sourceErrMsg,
  });

  const supplierName = useMakeSupplierName({});
  const scope1DataAbsolute = useScope1Data({
    isAbsoluteUnits: true,
    dropdownValues: useGetUnitDropdownValues(true),
  });
  const scope1DataIntensity = useScope1Data({
    isAbsoluteUnits: false,
    dropdownValues: useGetUnitDropdownValues(false),
  });
  const scope3DataAbsolute = useScope3Data({
    isAbsoluteUnits: true,
    dropdownValues: useGetUnitDropdownValues(true),
  });
  const scope3DataIntensity = useScope3Data({
    isAbsoluteUnits: false,
    dropdownValues: useGetUnitDropdownValues(false),
  });
  const scopeCombined13DataAbsolute = useScopeCombined13Data({
    label: 'Absolute emissions:',
    subLabel: 'Scope 1 and 3 (combined)',
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(true),
  });
  const scopeCombined13DataIntensity = useScopeCombined13Data({
    label: 'Supplier intensity data:',
    subLabel: 'Scope 1 and 3 (combined)',
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(false),
    errMsg: t('business:Errors.supplierForm.supplierIntensity'),
  });

  const howWasCollected = useHowWasCollected();

  const pageSchema = useMemo(() => {
    return [
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Supplier intensity data
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data received directly from suppliers based on carbon intensity/unit (e.g., kg CO
                  <sub>2</sub>e per units of product).
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Absolute emissions
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data received from suppliers as a total sum (total kg or tonnes CO<sub>2</sub>e) in relation
                  to products or services your business used.
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      // Shared between both views section
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        defaultValue: dataTypeIntensityValue,
        // propName: 'scope_1_3_behaviour_intensity',
        onChangeCallback: (e: string) => {
          setDataTypeIntensityValue(e);
        },
        ...dataTypeBehaviour,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        defaultValue: dataTypeEmissionsValue,
        // propName: 'scope_1_3_behaviour_absolute',
        onChangeCallback: (e: string) => {
          setDataTypeEmissionsValue(e);
        },
        ...dataTypeBehaviour,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...emissionSourceDropdown,
        onChangeCallback: (e: string) => {
          setEmissionSource(e);
        },
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...emissionSourceDropdown,
        onChangeCallback: (e: string) => {
          setEmissionSourceForAbsolute(e);
        },
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...supplierName,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...supplierName,
      },
      // NOW FOR intensity ONLY fields
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...scopeCombined13DataIntensity,
        renderMe: dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_COMBINED,
        saveMe: dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_COMBINED,
        validateMe: dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_COMBINED,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        renderMe:
          dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_SEPARATED ||
          dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_UNKNOWN,
        saveMe:
          dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_SEPARATED ||
          dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_UNKNOWN,
        validateMe:
          dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_SEPARATED ||
          dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_UNKNOWN,
        ...scope1DataIntensity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        renderMe: dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_SEPARATED,
        saveMe: dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_SEPARATED,
        validateMe: dataTypeIntensityValue === QUESTION_BRANCH_DATA_KEY_SEPARATED,
        ...scope3DataIntensity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        renderMe:
          dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_SEPARATED ||
          dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_UNKNOWN,
        saveMe:
          dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_SEPARATED ||
          dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_UNKNOWN,
        validateMe:
          dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_SEPARATED ||
          dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_UNKNOWN,
        ...scope1DataAbsolute,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        renderMe: dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_SEPARATED,
        saveMe: dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_SEPARATED,
        validateMe: dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_SEPARATED,
        ...scope3DataAbsolute,
      },
      // NOW FOR ABSOLUTE_ONLY field
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...scopeCombined13DataAbsolute,
        renderMe: dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_COMBINED,
        saveMe: dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_COMBINED,
        validateMe: dataTypeEmissionsValue === QUESTION_BRANCH_DATA_KEY_COMBINED,
      },

      // add the shared 'quantity + howWasCollected'
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...quantity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...quantityForAbsolute,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...howWasCollected,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...howWasCollected,
      },
      // inject the buttons
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_INTENSITY}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_INTENSITY);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_ABSOLUTE}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_ABSOLUTE);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
    ];
  }, [dataTypeEmissionsValue, dataTypeIntensityValue, emissionSource, mappedForDropdown, emissionSourceForAbsolute]);

  const [saveCallback, setSaveCallback] = useState<null | 'saveNew'>(null);
  const [questionGroupForValidation, setQuestionGroupForValidation] = useState<null | string>(null);

  return {
    pageSchema,
    saveCallback,
    questionGroupForValidation,
  };
}
